package org.moxultimate.topwithmox.dashboard

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.texts.DescriptionText
import org.moxultimate.topwithmox.components.texts.TitleText
import org.moxultimate.topwithmox.core.toggle.FeatureToggle.DASHBOARD_SHOWS_TOGGLE
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.utils.trackDashboardCardClick
import org.moxultimate.topwithmox.shows.data.ShowsResponse
import org.moxultimate.topwithmox.statics.DashboardStatics.COMING_SOON
import org.moxultimate.topwithmox.statics.DashboardStatics.COMING_SOON_DISPLAY
import org.moxultimate.topwithmox.statics.DashboardStatics.IMG_BG_WALLPAPER_SECTION
import org.moxultimate.topwithmox.statics.DashboardStatics.IMG_WOOD_CURVED
import org.moxultimate.topwithmox.statics.DashboardStatics.SHOW_ANIME_BUTTON
import org.moxultimate.topwithmox.statics.DashboardStatics.SHOW_ANIME_DESCRIPTION
import org.moxultimate.topwithmox.statics.DashboardStatics.SHOW_ANIME_LATEST_TEXT
import org.moxultimate.topwithmox.statics.DashboardStatics.SHOW_ANIME_PREVIEW_1_DESC
import org.moxultimate.topwithmox.statics.DashboardStatics.SHOW_ANIME_PREVIEW_1_TITLE
import org.moxultimate.topwithmox.statics.DashboardStatics.SHOW_ANIME_PREVIEW_1_URL
import org.moxultimate.topwithmox.statics.DashboardStatics.SHOW_ANIME_PREVIEW_2_DESC
import org.moxultimate.topwithmox.statics.DashboardStatics.SHOW_ANIME_PREVIEW_2_TITLE
import org.moxultimate.topwithmox.statics.DashboardStatics.SHOW_ANIME_PREVIEW_2_URL
import org.moxultimate.topwithmox.statics.DashboardStatics.SHOW_ANIME_PREVIEW_3_DESC
import org.moxultimate.topwithmox.statics.DashboardStatics.SHOW_ANIME_PREVIEW_3_TITLE
import org.moxultimate.topwithmox.statics.DashboardStatics.SHOW_ANIME_PREVIEW_3_URL
import org.moxultimate.topwithmox.statics.DashboardStatics.SHOW_ANIME_TITLE
import org.moxultimate.topwithmox.theme.FontFamily

/** Preview data for a single show/anime card — holds all display and interaction fields. */
private data class ShowPreviewItem(
    val imageUrl: String,
    val title: String,
    val description: String,
    val clickUrl: String,
)

/**
 * Builds the preview list from live API data when available, falling back to static placeholders.
 * The description slot uses the show's type; clickUrl uses the show's videoLink.
 *
 * @param shows Live shows list from the dashboard API, or null when still loading.
 * @return List of [ShowPreviewItem]s for the 3 preview cards.
 */
private fun buildShowPreviews(shows: List<ShowsResponse>?): List<ShowPreviewItem> =
    if (!shows.isNullOrEmpty()) {
        shows.map { show ->
            ShowPreviewItem(
                imageUrl = show.thumbnailPath ?: "",
                title = show.title ?: "",
                description = show.type ?: "",
                clickUrl = show.videoLink ?: "",
            )
        }
    } else {
        listOf(
            ShowPreviewItem(SHOW_ANIME_PREVIEW_1_URL, SHOW_ANIME_PREVIEW_1_TITLE, SHOW_ANIME_PREVIEW_1_DESC, ""),
            ShowPreviewItem(SHOW_ANIME_PREVIEW_2_URL, SHOW_ANIME_PREVIEW_2_TITLE, SHOW_ANIME_PREVIEW_2_DESC, ""),
            ShowPreviewItem(SHOW_ANIME_PREVIEW_3_URL, SHOW_ANIME_PREVIEW_3_TITLE, SHOW_ANIME_PREVIEW_3_DESC, ""),
        )
    }

/**
 * ShowAnimeSection
 * Dashboard section showcasing Top Shows & Anime on a wood-textured background.
 * Cards are clickable — opens the item's videoLink in a new tab.
 *
 * @param shows Live list of the 3 most recent shows from the dashboard API.
 *              Null while loading — falls back to static placeholder cards.
 */
@OptIn(DelicateApi::class)
@Composable
fun ShowAnimeSection(shows: List<ShowsResponse>? = null) {
    val breakpoint = rememberBreakpoint()
    val previews = buildShowPreviews(shows)

    when (breakpoint) {
        Breakpoint.ZERO, Breakpoint.SM, Breakpoint.MD -> ShowAnimeSectionMobile(previews)
        else -> ShowAnimeSectionDesktop(previews)
    }
}

/**
 * ShowAnimeSectionDesktop
 * Wood-textured background. 3-column grid of [ShowAnimePreviewCard]s.
 *
 * @param previews Resolved [ShowPreviewItem] list — live data or static fallback.
 */
@Composable
private fun ShowAnimeSectionDesktop(previews: List<ShowPreviewItem>) {
    val navigator = rememberAppNavigator()

    WoodSectionFrameDesktop {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween,
        ) {
            TitleText(title = SHOW_ANIME_TITLE)
            GradientButton(
                text = if (DASHBOARD_SHOWS_TOGGLE) SHOW_ANIME_BUTTON else COMING_SOON,
                buttonHeight = 50,
                disabled = !DASHBOARD_SHOWS_TOGGLE,
                onClick = { navigator.goToShowsPage() },
            )
        }

        DescriptionText(modifier = Modifier.padding(top = 16.px), description = SHOW_ANIME_DESCRIPTION)

        if (DASHBOARD_SHOWS_TOGGLE) {
            DescriptionText(
                description = SHOW_ANIME_LATEST_TEXT,
                modifier = Modifier.padding(top = 50.px, bottom = 16.px),
            )

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier { property("flex-wrap", "wrap") },
                horizontalArrangement = Arrangement.spacedBy(24.px),
                verticalAlignment = Alignment.Top,
            ) {
                previews.forEach { item ->
                    ShowAnimePreviewCard(
                        item = item,
                        modifier = Modifier
                            .styleModifier { property("width", "calc((100% - 48px) / 3)") }
                            .margin(bottom = 16.px),
                    )
                }
            }
        } else {
            Box(
                modifier = Modifier.fillMaxWidth().padding(top = 100.px, bottom = 50.px),
                contentAlignment = Alignment.Center,
            ) {
                TitleText(title = COMING_SOON_DISPLAY, modifier = Modifier.textAlign(TextAlign.Center))
            }
        }
    }
}

/**
 * ShowAnimePreviewCard
 * Clickable 16:9 show thumbnail with hover scale on image and hover opacity on text.
 * Opens [ShowPreviewItem.clickUrl] in a new tab on click; does nothing when the URL is blank.
 *
 * @param item     All display and interaction data for this card.
 * @param modifier Outer modifier — caller controls width and margin for grid placement.
 */
@Composable
private fun ShowAnimePreviewCard(
    item: ShowPreviewItem,
    modifier: Modifier = Modifier,
) {
    var isHovered by remember { mutableStateOf(false) }
    val isClickable = item.clickUrl.isNotBlank()

    Column(
        modifier = modifier
            .attrsModifier {
                onMouseEnter { isHovered = true }
                onMouseLeave { isHovered = false }
                if (isClickable) onClick {
                    trackDashboardCardClick("shows", item.title, item.clickUrl)
                    window.open(item.clickUrl, "_blank")
                }
            }
            .styleModifier {
                if (isClickable) property("cursor", "pointer")
            },
        horizontalAlignment = Alignment.Start,
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .borderRadius(12.px)
                .overflow(Overflow.Hidden)
                .styleModifier { property("aspect-ratio", "16 / 9") },
        ) {
            Image(
                src = item.imageUrl,
                description = item.title,
                modifier = Modifier
                    .fillMaxWidth()
                    .fillMaxHeight()
                    .styleModifier {
                        property("object-fit", "cover")
                        property("display", "block")
                        property("transition", "transform 0.3s ease, filter 0.3s ease")
                        property("transform", if (isHovered && isClickable) "scale(1.06)" else "scale(1.0)")
                        property("filter", if (isHovered && isClickable) "brightness(1.1)" else "brightness(1.0)")
                    }
                    .attrsModifier { attr("loading", "lazy") },
            )
        }

        SpanText(
            text = item.title,
            modifier = Modifier
                .padding(top = 10.px)
                .color(Colors.White)
                .fontFamily(FontFamily)
                .fontSize(14.px)
                .fontWeight(FontWeight.Bold)
                .styleModifier {
                    property("display", "inline-block")
                    property("transition", "transform 0.2s ease")
                    property("transform", if (isHovered && isClickable) "scale(1.12)" else "scale(1.0)")
                    property("transform-origin", "left center")
                },
        )

        if (item.description.isNotBlank()) {
            SpanText(
                text = item.description,
                modifier = Modifier
                    .padding(top = 4.px)
                    .color(Colors.White)
                    .fontFamily(FontFamily)
                    .fontSize(12.px)
                    .fontWeight(FontWeight.Normal),
            )
        }
    }
}


//----- Show & Anime Section Mobile ------------------------------------------------------------------------------------


/**
 * ShowAnimeSectionMobile
 * Wood-textured background: stacked title/description, horizontally scrollable card row, CTA button.
 *
 * @param previews Resolved [ShowPreviewItem] list — live data or static fallback.
 */
@OptIn(DelicateApi::class)
@Composable
private fun ShowAnimeSectionMobile(previews: List<ShowPreviewItem>) {
    val navigator = rememberAppNavigator()

    WoodSectionFrameMobile {
        Column(
            modifier = Modifier.fillMaxWidth().padding(leftRight = 40.px, bottom = 32.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            TitleText(title = SHOW_ANIME_TITLE, modifier = Modifier.textAlign(TextAlign.Center))
            DescriptionText(
                modifier = Modifier.padding(top = 24.px).textAlign(TextAlign.Center),
                description = SHOW_ANIME_DESCRIPTION,
            )
        }

        if (DASHBOARD_SHOWS_TOGGLE) {
            DescriptionText(
                description = SHOW_ANIME_LATEST_TEXT,
                modifier = Modifier.padding(left = 24.px, bottom = 12.px),
            )

            Row(
                modifier = WallpaperCarouselStyle.toModifier().fillMaxWidth().padding(leftRight = 16.px),
                horizontalArrangement = Arrangement.spacedBy(12.px),
                verticalAlignment = Alignment.Top,
            ) {
                previews.forEach { item ->
                    ShowAnimePreviewCard(
                        item = item,
                        modifier = Modifier.styleModifier {
                            property("width", "85vw")
                            property("flex-shrink", "0")
                        },
                    )
                }
            }
        }

        Box(
            modifier = Modifier.fillMaxWidth().padding(top = 32.px, bottom = 8.px),
            contentAlignment = Alignment.Center,
        ) {
            GradientButton(
                text = if (DASHBOARD_SHOWS_TOGGLE) SHOW_ANIME_BUTTON else COMING_SOON,
                disabled = !DASHBOARD_SHOWS_TOGGLE,
                onClick = { navigator.goToShowsPage() },
            )
        }
    }
}
